import {computed, ref} from 'vue'
import axios from 'axios'

import {useCompany} from './useCompany'
import {useGroupReporting} from './useGroupReporting'
import {usePermissions} from './usePermissions'
import {schemaHasVisibleFields} from '@/components/GroupReports/settings/schemaVisibility'

// Shown when a report fails to queue and the server gave nothing a person can
// read. Deliberately avoids claiming the failure was reported to us (it is not)
// and avoids inviting an immediate retry loop. Contact support only if it
// persists. This is the shared default for every report's submit path.
export const GENERIC_RUN_ERROR = 'Something went wrong generating this report. If this keeps happening, please contact support.'

// Collapse whatever a failed run returned into a flat array of message strings.
// A bodyless failure gives undefined; a Laravel validation error gives
// {message, errors: {field: [msg, ...]}}; some endpoints return a plain string.
// Consumers (the run-ready panel) join this array, so it must always be string[].
const normalizeRunErrors = (data) => {
    if (data === null || data === undefined) {
        return [GENERIC_RUN_ERROR]
    }

    if (typeof data === 'string') {
        return [data.trim() === '' ? GENERIC_RUN_ERROR : data]
    }

    if (data.errors && typeof data.errors === 'object') {
        const messages = Object.values(data.errors).flat()
        if (messages.length > 0) {
            return messages
        }
    }

    if (typeof data.message === 'string' && data.message.trim() !== '') {
        return [data.message]
    }

    return [GENERIC_RUN_ERROR]
}

/**
 * Shared report-building scaffold for the Group*Report.vue components
 * (ENGS-677). Owns the reactive state and the run/submit flow that every
 * report repeats; per-report variation is supplied through `options.extraParams`
 * and the report's own `run-options` slot.
 *
 * Follows the self-contained composable shape of useCompany/usePermissions:
 * it reads services internally and needs no `this`. The consuming component
 * still keeps SonicuCoreMixin for the parts outside report logic; both
 * permission systems read the same data (accepted dual pattern).
 *
 * @param schema  the report's settings schema (schemas/<report>.js)
 * @param options {
 *   interval?, defaultSettings?, extraParams?(state, reportTemplateId) => object,
 *   weekdayDefault?,            // if set, prepareSettings fills a missing
 *                               // `weekday` with this (scheduled reports default
 *                               // their send day, e.g. 1 = Monday)
 * }
 */
export function useGroupReport(schema, options = {}) {
    const {buildCompanyRoute} = useCompany()
    const {can} = usePermissions()

    // Whether this company is rolled onto group reporting (the `use-group-reporting`
    // option). Reactive: null while loading, then true/false. The report passes
    // this straight into GroupReportTemplate's `use-group-reporting-option` prop,
    // which drives whether the scheduled/template layer shows or the page goes
    // straight to the on-demand run form.
    const {groupReportingEnabled} = useGroupReporting()

    // Permission source for the derived settingsContext / hasManualRunSettings.
    // This is usePermissions()'s `can`, NOT the SonicuCoreMixin's this.permissions
    // -- the mixin instance is not reachable from setup(). For the common port
    // (a schema with NO permission-gated fields, e.g. Detailed Alarm / Alarm
    // Resolution) the source is irrelevant because `can` is never consulted. A
    // report that DOES gate a field on a permission must replace settingsContext
    // in its setup() return, after the `...report` spread. A component computed
    // of the same name does not work: Vue resolves setup state first, so the
    // computed never runs.
    const permissionCan = can

    const interval = ref(options.interval ?? 'monthly')
    const settings = ref(options.defaultSettings ?? {})
    const displaySection = ref(null)
    const selectedMeters = ref([])
    // Defaults to now, not null: on a manual run before the run form has
    // emitted an interval end, `end` must still post a real date so the
    // controller derives the window from the interval (matching the pre-composable
    // behaviour). MeterIntervalSelection overwrites this on interaction.
    const endOfInterval = ref(new Date())
    const runningReport = ref(false)
    const reportSuccessfullySent = ref(false)
    const errorSending = ref([])
    const reloadTable = ref(false)
    const groupId = ref(null)
    const templateName = ref('')
    const currentReportTemplateId = ref(null)
    const forGroup = ref(false)
    const useStandardReporting = ref(false)
    const data = ref([])

    // Passed to options.extraParams so a report can read run state (selected
    // meters, interval, its settings, a date ref it owns, etc.) when building
    // its own POST fields.
    const state = {
        interval, settings, displaySection, selectedMeters, endOfInterval,
        runningReport, reportSuccessfullySent, errorSending, reloadTable,
        groupId, templateName, currentReportTemplateId, forGroup,
        useStandardReporting, data,
    }

    const runReport = (reportTemplateId = null) => {
        const params = {}

        if (reportTemplateId !== null) {
            params.reportTemplateId = reportTemplateId
            params.forGroup = forGroup.value
        } else {
            params.interval = interval.value
        }

        params.end = endOfInterval.value
        params.meters = selectedMeters.value

        // Report-specific fields layered over the common base.
        // reportTemplateId is null for an On-Demand run.
        Object.assign(params, options.extraParams ? options.extraParams(state, reportTemplateId) : {})

        // Clear the previous outcome so a stale banner does not sit there while
        // the new request is in flight.
        reportSuccessfullySent.value = false
        errorSending.value = []
        runningReport.value = true

        return axios.post(buildCompanyRoute(`group-reports/${schema.slug}`), params)
            .then(() => {
                reportSuccessfullySent.value = true
                errorSending.value = []
            })
            .catch((error) => {
                // axios rejects with an Error, not the response: the body is at
                // error.response.data. Reading error.data (as several unported
                // reports did) gave undefined, so failures surfaced as nothing
                // at all. Keep the real detail in the console for debugging; the
                // UI shows only a short, human message.
                console.error(`${schema.slug} report failed to queue`, error.response?.data ?? error)
                // errorSending is consumed as an array of message strings (the
                // run-ready panel joins it), so normalise every failure shape -
                // a Laravel validation payload, a plain string, or nothing - down
                // to a flat string[] instead of leaking a raw object/string that
                // has no .join.
                errorSending.value = normalizeRunErrors(error.response?.data)
            })
            .finally(() => {
                runningReport.value = false
            })
    }

    const setUseStandardReporting = (value) => {
        useStandardReporting.value = value
    }

    const resetTemplateRun = () => {
        currentReportTemplateId.value = null
        forGroup.value = false
    }

    // The context the report's ReportSettingsPanel receives. Its `can` is the
    // report's own permission source (see permissionCan). Every port passed an
    // identical computed for this, so it lives here now.
    const settingsContext = computed(() => ({
        can: (permission) => permissionCan(permission),
    }))

    // True when the schema renders any field on a MANUAL run (isSavable: false).
    // A report whose only section is the template-only Scheduled Delivery has
    // nothing to configure manually, so GroupReportTemplate hides the "Report
    // Settings" button rather than opening an empty drawer. Reactive on settings
    // and interval so a showIf that depends on them re-evaluates.
    const hasManualRunSettings = computed(() => schemaHasVisibleFields(schema, {
        settings: settings.value,
        interval: interval.value,
        isSavable: false,
        can: (permission) => permissionCan(permission),
    }))

    // Fills a missing `weekday` with the report's default send day before a
    // template is saved/edited, matching the pre-schema settings components.
    // Only built when the report opts in via options.weekdayDefault; otherwise
    // null so GroupReportTemplate skips it.
    const prepareSettings = options.weekdayDefault === undefined
        ? null
        : (settingsValue) => {
            if (settingsValue['weekday'] === null || settingsValue['weekday'] === undefined) {
                settingsValue['weekday'] = options.weekdayDefault
            }
            return settingsValue
        }

    const base = {
        ...state,
        state,
        runReport,
        setUseStandardReporting,
        resetTemplateRun,
        settingsContext,
        hasManualRunSettings,
        groupReportingEnabled,
        schema,
    }

    // Only present when the report opts in with a weekdayDefault; otherwise
    // omitted so a port that does not want it can leave prepareSettingsFunction
    // unset. (settingsContext / hasManualRunSettings are always returned. A
    // report that needs a different value replaces the key in its setup()
    // return; see permissionCan above.)
    if (prepareSettings !== null) {
        base.prepareSettings = prepareSettings
    }

    return base
}
